import * as React from "react";
import { Alert, Button, Modal } from "antd";
import { MinusCircleOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { DomainPortInput } from "../../schema/types/domain/input";
import { formSetValue, useCpFormValue } from "../gvc/testNewForm/utils";
import { store } from "../../store";
import { get } from "lodash";
import { CpInput } from "../gvc/testNewForm/CpInput";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { parseLink } from "../../utils/linkParser/linkParser";

interface Props {
  portIndex: number;
  onClose: () => void;
}
export const PortRouteEditHeadersModal: React.FC<Props> = ({ portIndex, onClose }) => {
  const { id } = useCpFormContext();

  const port: DomainPortInput = useCpFormValue(`advanced.spec.ports.${portIndex}`);

  return (
    <Modal
      open={true}
      styles={{ body: { height: "70vh", padding: 0 } }}
      width={1000}
      title={`Edit Route Headers for Port ${port.number}`}
      onCancel={onClose}
      maskClosable={false}
      footer={
        <div className="flex items-center justify-end gap-4">
          <Button
            type="primary"
            onClick={() => {
              onClose();
            }}
          >
            OK
          </Button>
        </div>
      }
    >
      <div className="h-full overflow-auto">
        {port.routes.map((route, routeIndex) => {
          const workloadName = parseLink(route.workload.link, { useInputCtx: true }).name;

          return (
            <div key={route.id}>
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-2">
                  <div className="cursor-default">{`Workload: ${workloadName} ${
                    route.hostType === "prefix" ? `Prefix: ${route.prefix}` : `Regex: ${route.regex}`
                  }`}</div>
                  <Button
                    type="primary"
                    size={"small"}
                    onClick={() => {
                      const form = store.getState().form.instances[id];
                      const values = form.values;
                      const headers = get(values, `advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers`);
                      formSetValue(id, `advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers` as const, [
                        ...headers,
                        { id: uuidv4(), name: "", value: "" },
                      ]);
                    }}
                  >
                    Add
                  </Button>
                </div>
                {route.headers.length < 1 ? <div>No Items</div> : null}
                {route.headers.length > 0 ? (
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-xs color-label" style={{ minWidth: 450, maxWidth: 450 }}>Name</div>
                    <div className="text-xs color-label" style={{ minWidth: 450, maxWidth: 450 }}>Value</div>
                    <div style={{ width: 14 }} />
                  </div>
                ) : null}
                {route.headers.map((header, headerIndex) => {
                  const previousHeaders = route.headers.slice(0, headerIndex);
                  const hasSameName =
                    header.name && previousHeaders.some((ph) => ph.name.toLowerCase() === header.name.toLowerCase());

                  return (
                    <>
                      <div key={headerIndex} className="flex items-center gap-2 mb-1">
                        <CpInput
                          name={
                            `advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers.${headerIndex}.name` as const
                          }
                          style={{ minWidth: 450, maxWidth: 450 }}
                        />
                        <CpInput
                          name={
                            `advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers.${headerIndex}.value` as const
                          }
                          style={{ minWidth: 450, maxWidth: 450 }}
                        />
                        <MinusCircleOutlined
                          className="color-danger"
                          onClick={() => {
                            const form = store.getState().form.instances[id];
                            const values = form.values;
                            const headers = get(
                              values,
                              `advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers`,
                            );
                            const newHeaders = headers.filter((_, idx) => idx !== headerIndex);
                            formSetValue(
                              id,
                              `advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers` as const,
                              newHeaders,
                            );
                          }}
                        />
                      </div>
                      {hasSameName ? (
                        <Alert type={`warning`} message={`Overrides the previous header with the same name`} />
                      ) : null}
                    </>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
